import { jsx as _jsx } from "react/jsx-runtime";
import { MapLibreMap } from "@mapka/react-maplibre-map";
import { useAppSelector } from "../hooks/redux.js";
import { getCurrentSpec } from "../utils/selectors.js";
import { memo, useCallback, useEffect, useRef } from "react";
import { Flex } from "@mantine/core";
import { getApiKey } from "../reducers/apiKeyReducer.js";
import { isEqual } from "es-toolkit/compat";
import { useStyleEditor } from "./StyleEditorContext.js";
import { ErrorBoundary } from "./ErrorBoundary.js";
import { NavigationControl, ScaleControl } from "maplibre-gl";
import { Map as MapkaMap } from "@mapka/maplibre-gl-sdk";
MapkaMap.env = import.meta.env.VITE_DEPLOY_ENV;
const mapStyle = {
    border: "2px solid light-dark(black, white)",
    borderRadius: "12px",
    overflow: "hidden",
};
export const StyleEditorMap = memo(() => {
    const previous = useRef(null);
    const { map, setMap } = useStyleEditor();
    const apiKey = useAppSelector(getApiKey);
    const current = useAppSelector(getCurrentSpec);
    const handleLoad = useCallback((newMap) => {
        const nav = new NavigationControl({
            showCompass: false,
            showZoom: true,
        });
        const scale = new ScaleControl({
            maxWidth: 80,
            unit: "metric",
        });
        newMap.addControl(scale, "bottom-left");
        newMap.addControl(nav, "top-left");
        setMap(newMap);
    }, [setMap]);
    useEffect(() => {
        return () => {
            previous.current = current;
        };
    }, [current]);
    useEffect(() => {
        if (!map)
            return;
        if (!previous.current)
            return;
        if (!isEqual(previous.current.bearing, current.bearing)) {
            map.setBearing(current.bearing ?? 0);
        }
        if (!isEqual(previous.current.pitch, current.pitch)) {
            map.setPitch(current.pitch ?? 0);
        }
        if (!isEqual(previous.current.zoom, current.zoom)) {
            map.setZoom(current.zoom ?? 0);
        }
        if (!isEqual(previous.current.center, current.center)) {
            map.setCenter(current.center ?? [0, 0]);
        }
        if (!isEqual(previous.current.metadata?.mapka?.markers, current.metadata?.mapka?.markers)) {
            map.removeMarkers();
            map.removePopups();
            map.addMarkers(current.metadata?.mapka?.markers ?? []);
        }
    }, [map, current, previous]);
    return (_jsx(Flex, { h: "100%", flex: "1 1 50%", style: mapStyle, children: _jsx(ErrorBoundary, { children: _jsx(MapLibreMap, { BaseMap: MapkaMap, apiKey: apiKey, onMapLoaded: handleLoad, height: "100%", width: "100%", style: current }) }) }));
});
StyleEditorMap.displayName = "StyleMap";
